import React from "react";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { ConfigProvider } from "antd";
import { ColumnsType } from "antd/es/table";
import Table from "../../../commonComponents/Table";
import TablePagination from "../../../commonComponents/Pagination";
import {
  TableContainer,
  TableTitle,
  TableItemWrapper,
  TableItemImage,
  TableRowText,
} from "./styles";
import { ItemSalesTableProps, Item } from "./constants";
import { useTheme } from "../../../context/ThemeContext";
import { addCommasInPrices } from "./constants";
import { getMediaURL } from "../../../context/API";

const ItemSalesTable: React.FC<ItemSalesTableProps> = ({
  data,
  sortConfig,
  onSort,
  currentPage,
  itemsPerPage,
  totalItems,
  totalPages,
  onPageChange,
  onItemsPerPageChange,
  isLoading = false,
}) => {
  const { isDarkMode } = useTheme();

  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== itemsPerPage) {
      onItemsPerPageChange(size);
    }
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  const columns: ColumnsType<Item> = [
    {
      title: "Item",
      key: "item",
      sorter: true,
      sortOrder: sortConfig.key === "item" 
        ? (sortConfig.isAscending ? "ascend" : "descend") 
        : null,
      onHeaderCell: () => ({
        onClick: () => onSort("item"),
        style: { cursor: "pointer" },
      }),
      render: (_, record) => (
        <TableItemWrapper>
          <TableItemImage
            src={getMediaURL(record.imageUrl)}
            alt={record.item}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://via.placeholder.com/40";
            }}
          />
          <TableRowText>{record.item}</TableRowText>
        </TableItemWrapper>
      ),
    },
    {
      title: "Units Sold",
      dataIndex: "totalTimesSold",
      key: "totalTimesSold",
      align: "right",
      sorter: true,
      sortOrder: sortConfig.key === "totalTimesSold" 
        ? (sortConfig.isAscending ? "ascend" : "descend") 
        : null,
      onHeaderCell: () => ({
        onClick: () => onSort("totalTimesSold"),
        style: { cursor: "pointer" },
      }),
      render: (value) => <TableRowText>{value}</TableRowText>,
    },
    {
      title: "Revenue ($)",
      dataIndex: "totalRevenue",
      key: "totalRevenue",
      align: "right",
      sorter: true,
      sortOrder: sortConfig.key === "totalRevenue" 
        ? (sortConfig.isAscending ? "ascend" : "descend") 
        : null,
      onHeaderCell: () => ({
        onClick: () => onSort("totalRevenue"),
        style: { cursor: "pointer" },
      }),
      render: (value) => (
        <TableRowText>
          ${addCommasInPrices((parseFloat(String(value)) || 0).toFixed(2))}
        </TableRowText>
      ),
    },
  ];

  return (
    <StyledThemeProvider theme={styledTheme}>
      <ConfigProvider
        theme={{
          token: {
            colorBgContainer: isDarkMode ? "#2D3748" : "#ffffff",
            colorText: isDarkMode ? "#ffffff" : "#1A202C",
            colorBorder: isDarkMode ? "#4A5568" : "#E2E8F0",
          },
          components: {
            Pagination: {
              itemBg: isDarkMode ? "transparent" : "#ffffff",
              itemActiveBg: isDarkMode ? "#4a5568" : "#eee",
              itemLinkBg: "transparent",
              itemInputBg: isDarkMode ? "#2D3748" : "#ffffff",
              colorText: isDarkMode ? "#ffffff" : "#4e5156",
              colorTextDisabled: isDarkMode ? "#718096" : "#bfbfbf",
            },
            Select: {
              colorBgContainer: isDarkMode ? "#2D3748" : "#ffffff",
              colorText: isDarkMode ? "#ffffff" : "#4e5156",
              colorBorder: isDarkMode ? "#4A5568" : "#d9d9d9",
              colorPrimary: "#1890ff",
              optionSelectedBg: isDarkMode ? "#4a5568" : "#E6F4FF",
              optionActiveBg: isDarkMode ? "#4a5568" : "#E6F4FF",
            },
            Table: {
              headerBg: isDarkMode ? "#2D3748" : "#F7FAFC",
              headerColor: isDarkMode ? "#ffffff" : "#1A202C",
              colorTextHeading: isDarkMode ? "#ffffff" : "#1A202C",
              colorText: isDarkMode ? "#ffffff" : "#1A202C",
              colorTextSecondary: isDarkMode ? "#ffffff" : "#8c8c8c",
            },
            Empty: {
              colorText: isDarkMode ? "#ffffff" : "#8c8c8c",
              colorTextSecondary: isDarkMode ? "#ffffff" : "#8c8c8c",
            },
          },
        }}
      >
        <TableContainer>
          <TableTitle level={4}>Items Sold and Revenue</TableTitle>
          <Table<Item>
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={isLoading}
            pagination={false}
            emptyText="No items found"
            emptyDescription="No items available for the selected period"
            scroll={data.length > 0 ? { x: true, y: 400 } : undefined}
            headerBackgroundColor={isDarkMode ? "#2D3748" : "#F7FAFC"}
            removeVerticalBorders={true}
            thPadding="16px"
            tdPadding="16px"
            firstChildBorderRadius="12px"
            border="0"
            borderBottomLeftRight={true}
            theme={themeMode}
          />
          <TablePagination
            totalCount={totalItems}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onChange={handlePaginationChange}
            totalLabel="Total items"
            padding="24px 0 0 0"
          />
        </TableContainer>
      </ConfigProvider>
    </StyledThemeProvider>
  );
};

export default ItemSalesTable;
